import React, { forwardRef } from 'react';
import { cn } from '../../lib/utils';

type TableDensity = 'comfortable' | 'compact';

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  density?: TableDensity;
}

const Table = forwardRef<HTMLTableElement, TableProps>(({
  className,
  density = 'comfortable',
  ...props
}, ref) => <div className="relative w-full overflow-auto">
    <table ref={ref} data-density={density} className={cn('w-full caption-bottom text-sm', className)} {...props} />
  </div>);
Table.displayName = 'Table';
const TableHeader = forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(({
  className,
  ...props
}, ref) => <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />);
TableHeader.displayName = 'TableHeader';
const TableBody = forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(({
  className,
  ...props
}, ref) => <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />);
TableBody.displayName = 'TableBody';
const TableFooter = forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(({
  className,
  ...props
}, ref) => <tfoot ref={ref} className={cn('bg-primary font-medium text-primary-foreground', className)} {...props} />);
TableFooter.displayName = 'TableFooter';
const TableRow = forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(({
  className,
  ...props
}, ref) => <tr ref={ref} className={cn('border-b border-border/80 transition-colors hover:bg-muted/45 data-[state=selected]:bg-muted', className)} {...props} />);
TableRow.displayName = 'TableRow';
interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  density?: TableDensity;
}
const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(({
  className,
  density = 'comfortable',
  ...props
}, ref) => <th ref={ref} className={cn('text-left align-middle font-medium uppercase tracking-[0.06em] text-muted-foreground [&:has([role=checkbox])]:pr-0', density === 'compact' ? 'h-10 px-3 text-[11px]' : 'h-12 px-4 text-xs', className)} {...props} />);
TableHead.displayName = 'TableHead';
interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  density?: TableDensity;
}
const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(({
  className,
  density = 'comfortable',
  ...props
}, ref) => <td ref={ref} className={cn('align-middle [&:has([role=checkbox])]:pr-0', density === 'compact' ? 'px-3 py-2.5 text-sm' : 'p-4', className)} {...props} />);
TableCell.displayName = 'TableCell';
const TableCaption = forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(({
  className,
  ...props
}, ref) => <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />);
TableCaption.displayName = 'TableCaption';
export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };